import localforage from 'localforage';

import logger from '../Helpers/logger';

const DB_NAME = 'mirrorflydb';

/**
 * Key-value stores kept in the app database and wiped on logout.
 */
export const KEY_VALUE_STORE = {
    MESSAGE_OUTBOX: 'messageOutbox',
    CONTACT_CARDS: 'contactCards',
};

const STORE_DESCRIPTIONS = {
    [KEY_VALUE_STORE.MESSAGE_OUTBOX]: 'messages queued until the connection is back',
    [KEY_VALUE_STORE.CONTACT_CARDS]: 'shared contact card snapshots per message',
};

const stores = Object.fromEntries(
    Object.values(KEY_VALUE_STORE).map(storeName => [
        storeName,
        localforage.createInstance({ name: DB_NAME, storeName, description: STORE_DESCRIPTIONS[storeName] }),
    ]),
);

/**
 * Reads every value of a store, in no particular order.
 *
 * @param {string} storeName One of KEY_VALUE_STORE.
 * @returns {Promise<object[]>} The values; empty when the read fails.
 */
export const getAllItems = async storeName => {
    const items = [];
    try {
        await stores[storeName].iterate(value => {
            items.push(value);
        });
    } catch (error) {
        logger.warn(`[KV] getAllItems ${storeName} failed`, error);
    }
    return items;
};

/**
 * Reads one value.
 *
 * @param {string} storeName One of KEY_VALUE_STORE.
 * @param {string} key Key of the value.
 * @returns {Promise<object|null>} The value; null when missing or the read fails.
 */
export const getItem = async (storeName, key) => {
    try {
        return await stores[storeName].getItem(key);
    } catch (error) {
        logger.warn(`[KV] getItem ${storeName} failed`, error);
        return null;
    }
};

/**
 * Writes one value; a failed write is logged, not thrown.
 *
 * @param {string} storeName One of KEY_VALUE_STORE.
 * @param {string} key Key of the value.
 * @param {object} value Value to store.
 * @returns {Promise<void>}
 */
export const setItem = async (storeName, key, value) => {
    try {
        await stores[storeName].setItem(key, value);
    } catch (error) {
        logger.warn(`[KV] setItem ${storeName} failed`, error);
    }
};

/**
 * Removes one value; a failed removal is logged, not thrown.
 *
 * @param {string} storeName One of KEY_VALUE_STORE.
 * @param {string} key Key of the value.
 * @returns {Promise<void>}
 */
export const removeItem = async (storeName, key) => {
    try {
        await stores[storeName].removeItem(key);
    } catch (error) {
        logger.warn(`[KV] removeItem ${storeName} failed`, error);
    }
};

/**
 * Empties every key-value store, on logout.
 *
 * @returns {Promise<void[]>}
 */
export const clearKeyValueStores = () => Promise.all(Object.values(stores).map(store => store.clear()));
